import { GeolocationService } from "../services/geolocation-service";
import { CardBrand, FieldType } from "../types/enums";
import { Util } from "../utils/util";

export class ValidationRules {
    private static _validators: Map<FieldType, (value: string, extra?: any[]) => boolean> = new Map([
        [FieldType.PHONE, (v: string) => this._validatePhone(v)],
        [FieldType.EMAIL, (v: string) => this._validateEmail(v)],
        [FieldType.DAY, (v: string) => this._validateDay(v)],
        [FieldType.MONTH, (v: string) => this._validateMonth(v)],
        [FieldType.NAME, (v: string) => this._validateName(v)],
        [FieldType.CAR, (v: string) => this._validateCar(v)],
        [FieldType.PASSWORD, (v: string) => this._validatePassword(v)],
        [FieldType.COMPAREPASSWORDS, (_: string, extra?: any[]) => this._validateComparePassword(extra)],
        [FieldType.BOOLEAN, (v: string) => this._validateBoolean(v)],
        [FieldType.CUSTOM, (v: string, extra?: any[]) => this._validateCustom(v, extra?.[0] ?? (() => false))],
        [FieldType.TIP, (v: string) => this._validateTip(v)],
        [FieldType.FAILED, () => false],
        [FieldType.CREDIT_CARD_NUMBER, (v: string) => this._validateCreditCardNumber(v)],
        [FieldType.CREDIT_CARD_EXP, (_: string, extra?: any[]) => this._validateCreditCardExp(extra)],
        [FieldType.CREDIT_CARD_CVV_SIMPLE, (v: string) => this._validateCvvSimple(v)],
        [FieldType.CREDIT_CARD_CVV, (v: string, extra?: any[]) => this._validateCvv(v, extra)],
        [FieldType.CREDIT_CARD_ADDRESS, (v: string) => this._validateCreditCardAddress(v)],
        [FieldType.CREDIT_CARD_ZIP, (v: string) => this._validateCreditCardZipCode(v)],
        [FieldType.CHECKED, (v: string) => this._validateChecked(v)],
        [FieldType.GIFT_CARD_NUMBER, (v: string) => this._validateGiftCardNumber(v)],
        [FieldType.GIFT_CARD_PIN, (v: string) => this._validateGiftCardPin(v)],
        [FieldType.GIFT_CARD_PIN_VALUTEC, (v: string) => this._validateGiftCardPinValutec(v)],
        [FieldType.REQUIRED, (v: string) => this._validateRequired(v)],
    ]);

    private static _validatorsAsync: Map<FieldType, (value: string, geoService: GeolocationService) => Promise<boolean>> = new Map([
        [FieldType.ZIP, async (v, g) => await this._validateZipCodeAsync(v, g)],
    ]);

    public static validate(type: FieldType, value: string, extra?: any[]): boolean {
        const validator = this._validators.get(type);
        if (!validator) 
            return false;

        return validator(value.trim(), extra);
    }

    public static async validateAsync(type: FieldType, value: string, geoService: GeolocationService): Promise<boolean> {
        const validator = this._validatorsAsync.get(type);
        if (!validator) 
            return false;
        
        return await validator(value.trim(), geoService);
    }

    private static _validatePhone(input: string): boolean {
        const cleanInput = Util.cleanNonDigits(input); 
        return (cleanInput !== null) && (cleanInput !== "") && (cleanInput.length === 10);
    }

    private static _validateEmail(input: string): boolean {
        const filter = /^\s*[\w\-\+_]+(\.[\w\-\+_]+)*\@[\w\-\+_]+\.[\w\-\+_]+(\.[\w\-\+_]+)*\s*$/;
        return String(input.trim()).search(filter) != -1;
    }

    private static _validateDay(input: string): boolean {
        const n = Number(input);
        return !isNaN(n) && n >= 1 && n <= 31;
    }

    private static _validateMonth(input: string): boolean {
        const n = Number(input);
        return !isNaN(n) && n >= 1 && n <= 12;
    }

    private static _validateName(input: string): boolean {
        return input.trim() !== "";
    }

    private static _validateCar(input: string): boolean {
        return input.trim().length > 0;
    }

    private static _validatePassword(input: string): boolean {
        return Util.CheckIsPassword(input.trim());
    }

    private static _validateComparePassword(extra?: any[]): boolean {
        return !!extra && extra[0]?.trim() === extra[1]?.trim();
    }

    private static _validateBoolean(input: string): boolean {
        return input === "true";
    }

    private static _validateCustom(input: string, validator: (input: string) => boolean): boolean {
        return !!input && validator(input);
    }

    private static _validateTip(input: string): boolean {
        return Number(input) >= 0;
    }

    private static _validateCreditCardNumber(input: string): boolean {
        return Util.validateCardNumber(Util.cleanNonDigits(input)) > 0;
    }

    private static _validateCvvSimple(input: string): boolean {
        return Util.cleanNonDigits(input).length >= 3;
    }

    private static _validateCreditCardAddress(input: string): boolean {
        return input.length > 0;
    }

    private static _validateCreditCardZipCode(input: string): boolean {
        return input.length === 5 || input.length === 7;
    }

    private static _validateChecked(input: string): boolean {
        return input === "true";
    }

    private static _validateGiftCardNumber(input: string): boolean {
        return Util.cleanNonDigits(input).length >= 8;
    }

    private static _validateGiftCardPin(input: string): boolean {
        return Util.cleanNonDigits(input).length >= 4;
    }

    private static _validateGiftCardPinValutec(input: string): boolean {
        const length = Util.cleanNonDigits(input).length
        return length >= 1 && length <= 10;
    }

    private static _validateRequired(input: string): boolean {
        return input.trim().length !== 0;
    }

    private static _validateCvv(input: string, extra?: any[]): boolean {
        if (!extra || !extra[0]) 
            return false;

        const brand = Util.getCardBrand(extra[0]);
        const len = Util.cleanNonDigits(input).length;

        switch (brand) {
            case CardBrand.AMEX:
                return len === 4;
            case CardBrand.VISA:
            case CardBrand.MASTERCARD:
            case CardBrand.DISCOVER:
            case CardBrand.DINERSCLUB:
            case CardBrand.DINERS:
            case CardBrand.JCB:
                return len === 3;
            default:
                return false;
        }
    }

    private static _validateCreditCardExp(extra?: any[]): boolean {
        if (!extra || extra.length < 3) 
            return false;

        const [expMonth, expYear, offset] = extra.map(Number);
        const now = Util.NowStore(offset);
        const curMonth = now.getMonth() + 1;
        const curYear = now.getFullYear() % 100;

        return (
            expMonth > 0 &&
            expMonth <= 12 &&
            expYear > 0 &&
            !(curYear === expYear && expMonth < curMonth)
        );
    }

    private static async _validateZipCodeAsync(input: string, geoService: GeolocationService): Promise<boolean>{
        return await geoService.validateZip(input);
    }
}